import {
  Component,
  ChangeDetectionStrategy,
  computed,
  contentChild,
  effect,
  inject,
  input,
  output,
  untracked,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import {
  PageAction,
  PageHeaderComponent,
  PageHeaderIcon,
} from '../page-header/page-header.component';
import {
  BadgeVariant,
  FormDensityService,
  FormLayoutComponent,
  ToastContainerComponent,
  ToastConfig,
  NebulaToastService,
} from 'nebula-ui-kit';

@Component({
  selector: 'app-page-container',
  standalone: true,
  imports: [CommonModule, PageHeaderComponent, ToastContainerComponent],
  template: `
    <div class="page-container" [attr.data-nb-densidad]="densidadDeLaVista()">
      <div class="page-header-wrapper" [class.is-sticky]="stickyHeader()">
        <app-page-header
          [title]="title()"
          [subtitle]="subtitle()"
          [subtitleAsTag]="subtitleAsTag()"
          [pastilla]="pastilla()"
          [pastillaActiva]="pastillaActiva()"
          [pastillaVariante]="pastillaVariante()"
          [icon]="icon()"
          [actions]="actions()"
          [motorFormulario]="!!motor()"
          (actionClick)="action.emit($event)"
        >
          <ng-content select="[appCabeceraExtremo]" ngProjectAs="[appCabeceraExtremo]" />
        </app-page-header>
      </div>

      <div class="page-content">
        <ng-content></ng-content>
      </div>
    </div>

    <lib-toast-container #toastContainer></lib-toast-container>
  `,
  styleUrls: ['./page-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageContainerComponent implements OnDestroy {
  @ViewChild(ToastContainerComponent) toastContainer!: ToastContainerComponent;

  /** El motor de formulario dinamico, si la pantalla lo pinta dentro del contenedor. */
  protected readonly motor = contentChild(FormLayoutComponent, { descendants: true });

  private readonly densidades = inject(FormDensityService);

  /**
   * La densidad que el usuario eligio para el formulario a la vista, anunciada en TODA la vista
   * (ley de diseño del arquitecto, 23-oct-2026: «la densidad se aplica a toda la vista y afecta
   * todos los elementos»; Mantis #505). El perfil 3.0 del kit la aplica a cabecera, botones,
   * buscadores, tablas y pestañas. Normal, o una pantalla sin formulario, no anuncia nada.
   */
  protected readonly densidadDeLaVista = computed(() => {
    if (!this.motor()) return null;
    const densidad = this.densidades.densidad();
    return densidad === 'normal' ? null : densidad;
  });

  private destroy$ = new Subject<void>();

  title = input.required<string>();
  subtitle = input<string>();
  /** El subtitulo como etiqueta junto al titulo (ver `app-page-header`). */
  subtitleAsTag = input<boolean>(false);
  /** Pastilla con punto pulsante junto al titulo (p. ej. «2 tareas en curso»). */
  pastilla = input<string | null>(null);
  /** Pastilla viva o quieta. */
  pastillaActiva = input<boolean>(true);
  /** Color de la pastilla segun el estado que nombra (ver `app-page-header`). */
  pastillaVariante = input<BadgeVariant | null>(null);
  icon = input<PageHeaderIcon>();
  actions = input<PageAction[]>([]);
  stickyHeader = input(true);
  action = output<string>();

  /**
   * @deprecated El aviso dentro del contenedor se sustituye por el toast. Publicar con
   * `NebulaToastService`, que la zona de toast recoge. Se conserva mientras queden pantallas que
   * lo usen.
   */
  showAlert = input<boolean>(false);
  /**
   * @deprecated Parte del aviso dentro del contenedor, sustituido por el toast (Patron de
   * Notificaciones, 17-sep-2026). Publicar con `NebulaToastService`. Ver {@link showAlert}.
   */
  alertType = input<'error' | 'success' | 'warning' | 'info'>('info');
  /**
   * @deprecated Parte del aviso dentro del contenedor, sustituido por el toast (Patron de
   * Notificaciones, 17-sep-2026). Publicar con `NebulaToastService`. Ver {@link showAlert}.
   */
  alertTitle = input<string | undefined>('');
  /**
   * @deprecated Parte del aviso dentro del contenedor, sustituido por el toast (Patron de
   * Notificaciones, 17-sep-2026). Publicar con `NebulaToastService`. Ver {@link showAlert}.
   */
  alertMessage = input<string>('');
  /**
   * @deprecated Cierre del aviso dentro del contenedor, sustituido por el toast (Patron de
   * Notificaciones, 17-sep-2026): el toast se va solo y no avisa al cerrarse. Ver {@link showAlert}.
   */
  alertClosed = output<void>();

  private readonly avisos = inject(NebulaToastService);

  /** Ultimo aviso publicado, para no repetirlo mientras siga encendido. */
  private ultimoAviso: string | null = null;

  /** Titulos por defecto del AlertStore, escritos en español: en el toast el tipo lo dice el icono. */
  private static readonly TITULOS_FIJOS = ['Error', 'Éxito', 'Advertencia', 'Información'];

  constructor() {
    // El aviso que una pantalla todavia enciende con [showAlert] ya no se pinta dentro del
    // contenedor: se publica en la zona de toast (orden del arquitecto, 29-sep-2026; Mantis #212).
    // El estado del aviso sigue en su store, porque la pantalla lo usa para saber si un guardado
    // fallo; aqui solo cambia donde se ve.
    effect(() => {
      const encendido = this.showAlert();
      const tipo = this.alertType();
      const mensaje = this.alertMessage();
      const titulo = this.alertTitle();
      untracked(() => {
        if (!encendido || !mensaje) {
          this.ultimoAviso = null;
          return;
        }
        const clave = `${tipo}|${titulo ?? ''}|${mensaje}`;
        if (clave === this.ultimoAviso) return;
        this.ultimoAviso = clave;
        this.avisos.show({
          type: tipo,
          message: mensaje,
          title:
            titulo && !PageContainerComponent.TITULOS_FIJOS.includes(titulo) ? titulo : undefined,
        });
      });
    });
  }

  showToast(config: ToastConfig): void {
    if (this.toastContainer) {
      this.toastContainer.show(config);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.alertClosed.emit();
  }
}
